import { PostFormatToggle } from "./PostFormatToggle";
import {
  Instagram,
  Loader2,
  LucideIcon,
  RotateCcw,
  Youtube,
} from "lucide-react";
import { useEffect, useState } from "react";
import { IconType } from "react-icons";
import { FaFacebook, FaLinkedin, FaPinterest, FaTiktok } from "react-icons/fa6";
import { useAuth } from "../contexts/AuthContext";
import {
  getEffectivePlatformDescription,
  getInheritedPlatformDescription,
  PINTEREST_CAPTION_LIMIT,
} from "../lib/platformCaptions";
import { cn } from "../lib/utils";
import { validateMediaForPlatform } from "../lib/validate";
import { PlatformConnection, PlatformType } from "../models/Platform";
import {
  VideoSettingsOverrides,
  FacebookSettings,
  InstagramSettings,
  LinkedinSettings,
  MediaItem,
  PlatformSpecificSettings,
  PinterestSettings,
  TiktokSettings,
  YoutubeSettings,
} from "../models/Post";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Switch } from "./ui/switch";
import { RichCaptionTextarea } from "./RichCaptionTextarea";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";

type Platform = {
  name: string;
  icon: LucideIcon | IconType;
  color: string;
  settings: string[];
  requirements: string;
};

// Define default settings for each platform
export const defaultSettings: Record<
  PlatformType,
  PlatformSpecificSettings[keyof PlatformSpecificSettings]
> = {
  tiktok: {
    privacy: "PUBLIC_TO_EVERYONE",
    commercialContentEnabled: false,
    yourBrand: false,
    brandedContent: false,
    allowComments: true,
    allowDuet: true,
    allowStitch: true,
    aiGeneratedContent: false,
    description: "",
    title: undefined,
  },
  youtube: {
    privacy: "public",
    madeForKids: false,
    description: undefined,
    title: undefined,
  },
  instagram: {
    postType: "feed",
    description: undefined,
  },
  linkedin: {
    title: undefined,
    description: undefined,
  },
  facebook: {
    description: undefined,
    postType: "feed",
  },
  pinterest: {
    title: undefined,
    description: undefined,
    descriptionSource: undefined,
    boardId: undefined,
    boardName: undefined,
    link: undefined,
  },
};

// Per-platform caption character limits (from each platform's API)
export const PLATFORM_CAPTION_LIMITS: Record<PlatformType, number> = {
  instagram: 2200,
  linkedin: 3000,
  tiktok: 4000,
  youtube: 5000,
  facebook: 63206,
  pinterest: PINTEREST_CAPTION_LIMIT,
};

// Define validation rules per platform
type ValidationRule = {
  check: (
    settings: PlatformSpecificSettings[keyof PlatformSpecificSettings],
    postData?: { postTitle?: string; postDescription?: string }
  ) => boolean;
  message: string;
};

function getEffectiveDescriptionLength(
  platform: PlatformType,
  settings: PlatformSpecificSettings[keyof PlatformSpecificSettings],
  postDescription?: string
): number {
  const desc = (settings as { description?: string }).description;
  return getEffectivePlatformDescription(
    platform,
    desc,
    postDescription || ""
  ).length;
}

function makeCaptionLimitRule(platform: PlatformType): ValidationRule {
  const limit = PLATFORM_CAPTION_LIMITS[platform];
  return {
    check: (settings, postData) =>
      getEffectiveDescriptionLength(
        platform,
        settings,
        postData?.postDescription
      ) <= limit,
    message: `Caption exceeds ${limit.toLocaleString()} character limit`,
  };
}

const platformValidations: Record<
  PlatformType,
  {
    requiredFields: ValidationRule[];
    conditionalRules: ValidationRule[];
  }
> = {
  tiktok: {
    requiredFields: [
      {
        check: (settings, postData?: { postTitle?: string }): boolean => {
          const tikTokSettings = settings as TiktokSettings;
          return !!tikTokSettings.privacy;
        },
        message: "Choose who can see your post",
      },
    ],
    conditionalRules: [
      {
        check: (settings, postData?: { postTitle?: string }): boolean => {
          const tikTokSettings = settings as TiktokSettings;
          return (
            !tikTokSettings.commercialContentEnabled ||
            tikTokSettings.yourBrand ||
            tikTokSettings.brandedContent
          );
        },
        message:
          "You need to indicate if your content promotes yourself, a third party, or both.",
      },
      makeCaptionLimitRule("tiktok"),
    ],
  },
  youtube: {
    requiredFields: [
      {
        check: (settings, postData?: { postTitle?: string }): boolean => {
          const youtubeSettings = settings as YoutubeSettings;
          return !!youtubeSettings.title || !!postData?.postTitle;
        },
        message:
          "YouTube title is required. Please provide a title below or ensure a general post title is set.",
      },
    ],
    conditionalRules: [makeCaptionLimitRule("youtube")],
  },
  instagram: {
    requiredFields: [],
    conditionalRules: [makeCaptionLimitRule("instagram")],
  },
  linkedin: {
    requiredFields: [],
    conditionalRules: [makeCaptionLimitRule("linkedin")],
  },
  facebook: {
    requiredFields: [],
    conditionalRules: [makeCaptionLimitRule("facebook")],
  },
  pinterest: {
    requiredFields: [
      {
        check: (settings): boolean => {
          const pinterestSettings = settings as PinterestSettings;
          return !!pinterestSettings.boardId;
        },
        message: "Choose a Pinterest board",
      },
    ],
    conditionalRules: [makeCaptionLimitRule("pinterest")],
  },
};

/**
 * Everything that blocks publishing one connection: platform setting rules
 * plus media requirements. The publish modal shows these per channel.
 */
export function getConnectionErrors({
  connection,
  settings,
  mediaItems,
  isVideoPost = false,
  postTitle,
  postDescription,
}: {
  connection: PlatformConnection;
  settings: PlatformSpecificSettings[keyof PlatformSpecificSettings] | undefined;
  mediaItems: MediaItem[];
  isVideoPost?: boolean;
  postTitle?: string;
  postDescription?: string;
}): string[] {
  const rules = platformValidations[connection.platform];
  const effective = settings || defaultSettings[connection.platform];
  if (!rules || !effective) return [];
  const postData = { postTitle, postDescription };
  const errors = [...rules.requiredFields, ...rules.conditionalRules]
    .filter((rule) => !rule.check(effective, postData))
    .map((rule) => rule.message);
  const media = validateMediaForPlatform(
    mediaItems,
    connection.platform,
    (effective as VideoSettingsOverrides).videoEnabled ?? isVideoPost
  );
  if (!media.isValid) {
    errors.push(
      ...media.errors.map((error) =>
        error.mediaIndex !== undefined
          ? `Media ${error.mediaIndex + 1}: ${error.message}`
          : error.message
      )
    );
  }
  return errors;
}

export const getPlatformConfig = (platformType: PlatformType): Platform => {
  return {
    instagram: {
      name: "Instagram",
      icon: Instagram,
      color: "text-black",
      settings: ["description", "hashtags", "location", "music"],
      requirements: "Vertical video (9:16), 15-60 seconds",
    },
    youtube: {
      name: "YouTube",
      icon: Youtube,
      color: "text-black",
      settings: ["title", "description", "tags", "playlist"],
      requirements:
        "Vertical video (9:16); image carousels publish as a video slideshow",
    },
    tiktok: {
      name: "TikTok",
      icon: FaTiktok,
      color: "text-black",
      settings: ["description", "hashtags", "duet", "stitch"],
      requirements: "Images or video; video duration depends on your TikTok account",
    },
    linkedin: {
      name: "LinkedIn",
      icon: FaLinkedin,
      color: "text-black",
      settings: ["description", "hashtags", "duet", "stitch"],
      requirements: "Vertical video (9:16), up to 60s",
    },
    facebook: {
      name: "Facebook",
      icon: FaFacebook,
      color: "text-black",
      settings: ["description"],
      requirements: "Video or image, various aspect ratios supported",
    },
    pinterest: {
      name: "Pinterest",
      icon: FaPinterest,
      color: "text-black",
      settings: ["board", "title", "description", "link"],
      requirements:
        "Images only; 2-5 publish as carousel, 6+ use the first image",
    },
  }[platformType];
};

/**
 * A per-channel title that follows the post title until the user types over
 * it. The post title sits in the field, muted; focusing it makes it ordinary
 * editable text, and it only becomes an override once it differs.
 */
function InheritedTitleField({
  id,
  label,
  value,
  postTitle,
  onChange,
  maxLength,
  placeholder,
  emptyHint,
}: {
  id: string;
  label: string;
  value: string | undefined;
  postTitle?: string;
  onChange: (title: string | undefined) => void;
  maxLength: number;
  placeholder: string;
  emptyHint?: string;
}) {
  const isInherited = !value && !!postTitle;
  const shown = value || postTitle || "";
  return (
    <div className="space-y-2">
      <div className="flex min-h-8 items-center justify-between gap-2">
        <Label htmlFor={id}>{label}</Label>
        {!!value && !!postTitle && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs text-muted-foreground"
            onClick={() => onChange(undefined)}
          >
            <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
            Use post title
          </Button>
        )}
      </div>
      <Input
        id={id}
        className={cn(
          "notranslate",
          isInherited &&
            "border-transparent bg-muted/70 text-muted-foreground hover:border-input focus-visible:bg-background focus-visible:text-foreground"
        )}
        translate="no"
        value={shown}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          const next = e.target.value;
          onChange(next === "" || next === postTitle ? undefined : next);
        }}
        placeholder={placeholder}
        maxLength={maxLength}
      />
      <div className="flex items-center justify-between text-xs">
        <span className="text-muted-foreground italic">
          {isInherited ? "Same as post. Type to override." : !shown ? emptyHint : ""}
        </span>
        <span className="text-muted-foreground">
          {shown.length} / {maxLength}
        </span>
      </div>
    </div>
  );
}

function PlatformDescriptionField({
  platform,
  connectionId,
  description,
  descriptionSource,
  postDescription,
  onDescriptionChange,
  isGenerating = false,
}: {
  platform: string;
  connectionId: string;
  description: string | undefined;
  descriptionSource?: "generated" | "manual";
  postDescription: string;
  onDescriptionChange: (description: string) => void;
  isGenerating?: boolean;
}) {
  const platformType = platform as PlatformType;
  const platformConfig = getPlatformConfig(platformType);
  const isInherited = !description?.trim();
  const inheritedDescription = getInheritedPlatformDescription(
    platformType,
    postDescription
  );
  const effectiveDescription = isInherited
    ? inheritedDescription
    : description || "";
  const wasAdapted = isInherited && inheritedDescription !== postDescription;
  const effectiveLength = effectiveDescription.length;
  const limit = PLATFORM_CAPTION_LIMITS[platformType];
  const isOver = effectiveLength > limit;
  const isNear = !isOver && effectiveLength > limit * 0.9;

  return (
    <div className="space-y-2">
      <div className="flex min-h-8 items-center justify-between gap-2">
        <Label htmlFor={`${platform}-desc-${connectionId}`}>Description</Label>
        {!isInherited && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs text-muted-foreground"
            onClick={() => onDescriptionChange("")}
          >
            <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
            Use post caption
          </Button>
        )}
      </div>
      <RichCaptionTextarea
        id={`${platform}-desc-${connectionId}`}
        value={effectiveDescription}
        onChange={onDescriptionChange}
        placeholder={
          postDescription
            ? undefined
            : `Enter description for ${platformConfig.name}`
        }
        maxLength={platformType === "pinterest" ? limit : undefined}
        textareaClassName={cn(
          "min-h-[120px] resize-y",
          isInherited &&
            !!postDescription &&
            "border-transparent bg-muted/70 text-muted-foreground hover:border-input focus-visible:bg-background focus-visible:text-foreground"
        )}
      />
      <div className="flex items-center justify-between text-xs">
        <span className="text-muted-foreground italic">
          {isGenerating ? (
            <span className="inline-flex items-center gap-1">
              <Loader2 className="h-3 w-3 animate-spin" />
              Shortening to fit Pinterest...
            </span>
          ) : wasAdapted
            ? "Shortened from post caption"
            : isInherited && postDescription
              ? "Same as post. Type to override."
            : !isInherited
                ? descriptionSource === "generated"
                  ? "Shortened to fit Pinterest's limit"
                  : `Custom ${platformConfig.name} caption`
                : ""}
        </span>
        <span
          className={cn(
            isOver
              ? "text-red-500 font-medium"
              : isNear
                ? "text-yellow-600"
                : "text-muted-foreground"
          )}
        >
          {effectiveLength.toLocaleString()} / {limit.toLocaleString()}
        </span>
      </div>
    </div>
  );
}

function TikTokConnectionSettings({
  connection,
  mediaItems,
  settings,
  onSettingsChange,
  postDescription,
  postTitle,
}: {
  connection: PlatformConnection;
  mediaItems: MediaItem[];
  settings: TiktokSettings;
  onSettingsChange: (settings: Partial<TiktokSettings>) => void;
  postDescription: string;
  postTitle?: string;
}) {
  const isVideo = mediaItems[0]?.type === "video";
  const hasBrandedContent =
    settings.commercialContentEnabled && settings.brandedContent;
  const isCarousel = mediaItems.length > 1;

  return (
    <div className="space-y-4">
      {/* Title Field (Conditional for Carousel) */}
      {isCarousel && (
        <InheritedTitleField
          id={`tiktok-title-${connection.id}`}
          label="Title"
          value={settings.title}
          postTitle={postTitle}
          onChange={(title) => onSettingsChange({ title })}
          maxLength={90}
          placeholder="Enter TikTok title"
        />
      )}

      {/* Description Field */}
      <PlatformDescriptionField
        platform="tiktok"
        connectionId={connection.id}
        description={settings.description}
        postDescription={postDescription}
        onDescriptionChange={(desc) => onSettingsChange({ description: desc })}
      />
      {/* Privacy Settings */}
      <div className="space-y-2">
        <Label>Privacy</Label>
        <Select
          value={settings.privacy}
          onValueChange={(value) => onSettingsChange({ privacy: value })}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Choose who can view this post" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="PUBLIC_TO_EVERYONE">Public</SelectItem>
            <SelectItem value="MUTUAL_FOLLOW_FRIENDS">Friends</SelectItem>
            <div className="relative">
              <SelectItem value="SELF_ONLY" disabled={hasBrandedContent}>
                Private
              </SelectItem>
              {hasBrandedContent && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="absolute inset-0" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Branded content visibility cannot be set to private</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          </SelectContent>
        </Select>
      </div>

      {/* Interaction Settings */}
      <div className="space-y-2">
        <Label>Interactions</Label>
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Checkbox
              id={`comments-${connection.id}`}
              checked={settings.allowComments}
              onCheckedChange={(checked) =>
                onSettingsChange({ allowComments: checked === true })
              }
            />
            <Label htmlFor={`comments-${connection.id}`}>Allow comments</Label>
          </div>

          {isVideo && (
            <>
              <div className="flex items-center gap-2">
                <Checkbox
                  id={`duet-${connection.id}`}
                  checked={settings.allowDuet}
                  onCheckedChange={(checked) =>
                    onSettingsChange({ allowDuet: checked === true })
                  }
                />
                <Label htmlFor={`duet-${connection.id}`}>Allow Duet</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id={`stitch-${connection.id}`}
                  checked={settings.allowStitch}
                  onCheckedChange={(checked) =>
                    onSettingsChange({ allowStitch: checked === true })
                  }
                />
                <Label htmlFor={`stitch-${connection.id}`}>Allow Stitch</Label>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Commercial Content */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label>Commercial Content</Label>
          <Switch
            checked={settings.commercialContentEnabled}
            onCheckedChange={(checked) => {
              onSettingsChange({
                commercialContentEnabled: checked,
                // Reset brand options when disabling
                ...(checked
                  ? {}
                  : {
                      yourBrand: false,
                      brandedContent: false,
                    }),
              });
            }}
          />
        </div>

        {settings.commercialContentEnabled && (
          <div className="ml-2 space-y-2">
            <div className="flex items-center gap-2">
              <Checkbox
                id={`your-brand-${connection.id}`}
                checked={settings.yourBrand}
                onCheckedChange={(checked) =>
                  onSettingsChange({ yourBrand: checked === true })
                }
              />
              <div>
                <Label htmlFor={`your-brand-${connection.id}`}>
                  Your Brand
                </Label>
                <p className="text-sm text-muted-foreground">
                  You are promoting yourself or your own business.
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id={`branded-content-${connection.id}`}
                checked={settings.brandedContent}
                onCheckedChange={(checked) => {
                  const isBrandedContent = checked === true;
                  onSettingsChange({
                    brandedContent: isBrandedContent,
                    // If enabling branded content and privacy is private, switch to public
                    ...(isBrandedContent && settings.privacy === "SELF_ONLY"
                      ? { privacy: "PUBLIC_TO_EVERYONE" }
                      : {}),
                  });
                }}
              />
              <div>
                <Label htmlFor={`branded-content-${connection.id}`}>
                  Branded Content
                </Label>
                <p className="text-sm text-muted-foreground">
                  You are promoting another brand or a third party.
                </p>
                {settings.privacy === "SELF_ONLY" && (
                  <p className="text-xs text-yellow-600 mt-1">
                    Enabling this will change visibility to public
                  </p>
                )}
              </div>
            </div>
            {(settings.yourBrand || settings.brandedContent) && (
              <p className="text-sm text-muted-foreground">
                {settings.brandedContent
                  ? "Your photo/video will be labeled as 'Paid partnership'"
                  : "Your photo/video will be labeled as 'Promotional content'"}
              </p>
            )}
          </div>
        )}
      </div>
      <div className="flex items-center gap-2">
        <Switch
          checked={settings.aiGeneratedContent}
          onCheckedChange={(checked) => {
            onSettingsChange({
              aiGeneratedContent: checked,
            });
          }}
        />
        <div>
          <Label htmlFor={`ai-generated-content-${connection.id}`}>
            AI-Generated Content
          </Label>
          <p className="text-sm text-muted-foreground">
            This content was generated using AI tools.
          </p>
        </div>
      </div>
      {/* Terms Agreement */}
      <div className="flex items-center gap-2">
        <Label
          htmlFor={`terms-${connection.id}`}
          className="text-sm text-muted-foreground"
        >
          By posting, you agree to TikTok's{" "}
          <a
            href="https://www.tiktok.com/legal/page/global/music-usage-confirmation/en"
            target="_blank"
            rel="noopener noreferrer"
            className="text-brand hover:text-brand/80 underline"
          >
            Music Usage Confirmation
          </a>
          {settings.brandedContent && (
            <>
              {" "}
              and{" "}
              <a
                href="https://www.tiktok.com/legal/page/global/bc-policy/en"
                target="_blank"
                rel="noopener noreferrer"
                className="text-brand hover:text-brand/80 underline"
              >
                Branded Content Policy
              </a>
            </>
          )}
        </Label>
      </div>
    </div>
  );
}

function YoutubeConnectionSettings({
  connection,
  mediaItems,
  settings,
  onSettingsChange,
  postDescription,
  postTitle,
}: {
  connection: PlatformConnection;
  mediaItems: MediaItem[];
  settings: YoutubeSettings;
  onSettingsChange: (settings: Partial<YoutubeSettings>) => void;
  postDescription: string;
  postTitle?: string;
}) {

  return (
    <div className="space-y-4">
      {/* Title Field */}
      <InheritedTitleField
        id={`youtube-title-${connection.id}`}
        label="Title"
        value={settings.title}
        postTitle={postTitle}
        onChange={(title) => onSettingsChange({ title })}
        maxLength={90}
        placeholder="Enter YouTube title (Required)"
        emptyHint="A title is required for YouTube."
      />

      {/* Description Field */}
      <PlatformDescriptionField
        platform="youtube"
        connectionId={connection.id}
        description={settings.description}
        postDescription={postDescription}
        onDescriptionChange={(desc) => onSettingsChange({ description: desc })}
      />
      {/* Privacy Settings */}
      <div className="space-y-2">
        <Label>Privacy</Label>
        <Select
          value={settings.privacy}
          onValueChange={(value: "public" | "unlisted" | "private") =>
            onSettingsChange({ privacy: value })
          }
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Choose who can view this post" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="public">Public</SelectItem>
            <SelectItem value="unlisted">Unlisted</SelectItem>
            <SelectItem value="private">Private</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Made for Kids Setting */}
      <div className="flex items-center gap-2">
        <Switch
          checked={settings.madeForKids}
          onCheckedChange={(checked) => {
            onSettingsChange({
              madeForKids: checked,
            });
          }}
        />
        <div>
          <Label htmlFor={`made-for-kids-${connection.id}`}>
            Made for Kids
          </Label>
        </div>
      </div>

    </div>
  );
}

function InstagramConnectionSettings({
  connection,
  mediaItems,
  settings,
  onSettingsChange,
  postDescription,
}: {
  connection: PlatformConnection;
  mediaItems: MediaItem[];
  settings: InstagramSettings;
  onSettingsChange: (settings: Partial<InstagramSettings>) => void;
  postDescription: string;
}) {
  const isCarousel = mediaItems.length > 1;

  return (
    <div className="space-y-4">
      {/* Description Field */}
      <PlatformDescriptionField
        platform="instagram"
        connectionId={connection.id}
        description={settings.description}
        postDescription={postDescription}
        onDescriptionChange={(desc) => onSettingsChange({ description: desc })}
      />
      {/* Post Type Selection */}
      <div className="space-y-2">
        <Label>Post Type</Label>
        <RadioGroup
          value={settings.postType || "feed"}
          onValueChange={(value: "feed" | "story") =>
            onSettingsChange({ postType: value })
          }
          className="flex flex-col space-y-1"
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="feed" id={`feed-${connection.id}`} />
            <Label htmlFor={`feed-${connection.id}`}>Feed</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="story" id={`story-${connection.id}`} />
            <Label htmlFor={`story-${connection.id}`}>Story</Label>
            {isCarousel && (
              <span className="text-xs text-muted-foreground ml-2 italic">
                (items will post as separate stories)
              </span>
            )}
          </div>
        </RadioGroup>
      </div>

      {/* Future Instagram-specific settings can be added here */}
    </div>
  );
}

function LinkedinConnectionSettings({
  connection,
  mediaItems,
  settings,
  onSettingsChange,
  postDescription,
  postTitle,
}: {
  connection: PlatformConnection;
  mediaItems: MediaItem[];
  settings: LinkedinSettings;
  onSettingsChange: (settings: Partial<LinkedinSettings>) => void;
  postDescription: string;
  postTitle?: string;
}) {
  const hasMultipleImages =
    mediaItems.filter((item) => item.type === "image").length > 1;

  return (
    <div className="space-y-4">
      {/* Title Field - shown when there are multiple images (PDF carousel) */}
      {hasMultipleImages && (
        <InheritedTitleField
          id={`linkedin-title-${connection.id}`}
          label="Document Title"
          value={settings.title}
          postTitle={postTitle}
          onChange={(title) => onSettingsChange({ title })}
          maxLength={100}
          placeholder="Enter document title for LinkedIn carousel"
          emptyHint="Optional. Used as PDF document title."
        />
      )}

      {/* Description Field */}
      <PlatformDescriptionField
        platform="linkedin"
        connectionId={connection.id}
        description={settings.description}
        postDescription={postDescription}
        onDescriptionChange={(desc) => onSettingsChange({ description: desc })}
      />

    </div>
  );
}

function FacebookConnectionSettings({
  connection,
  mediaItems,
  settings,
  onSettingsChange,
  postDescription,
}: {
  connection: PlatformConnection;
  mediaItems: MediaItem[];
  settings: FacebookSettings;
  onSettingsChange: (settings: Partial<FacebookSettings>) => void;
  postDescription: string;
}) {
  const isCarousel = mediaItems.length > 1;
  const hasVideo = mediaItems.some((item) => item.type === "video");

  return (
    <div className="space-y-4">
      {/* Description Field */}
      <PlatformDescriptionField
        platform="facebook"
        connectionId={connection.id}
        description={settings.description}
        postDescription={postDescription}
        onDescriptionChange={(desc) => onSettingsChange({ description: desc })}
      />

      {/* Post Type Selection */}
      <div className="space-y-2">
        <Label>Post Type</Label>
        <RadioGroup
          value={settings.postType || "feed"}
          onValueChange={(value: "feed" | "story") =>
            onSettingsChange({ postType: value })
          }
          className="flex flex-col space-y-1"
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="feed" id={`feed-${connection.id}`} />
            <Label htmlFor={`feed-${connection.id}`}>Feed</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem
              value="story"
              id={`story-${connection.id}`}
              disabled={hasVideo}
            />
            <Label
              htmlFor={`story-${connection.id}`}
              className={hasVideo ? "text-muted-foreground" : ""}
            >
              Story
            </Label>
            {hasVideo && (
              <span className="text-xs text-muted-foreground ml-2 italic">
                (not available for video)
              </span>
            )}
            {isCarousel && !hasVideo && (
              <span className="text-xs text-muted-foreground ml-2 italic">
                (items will post as separate stories)
              </span>
            )}
          </div>
        </RadioGroup>
      </div>
    </div>
  );
}

type PinterestBoard = {
  id: string;
  name: string;
};

function PinterestConnectionSettings({
  connection,
  mediaItems,
  settings,
  onSettingsChange,
  postDescription,
  postTitle,
  defaultPinterestLink,
  isDescriptionGenerating = false,
}: {
  connection: PlatformConnection;
  mediaItems: MediaItem[];
  settings: PinterestSettings;
  onSettingsChange: (settings: Partial<PinterestSettings>) => void;
  postDescription: string;
  postTitle?: string;
  defaultPinterestLink?: string;
  isDescriptionGenerating?: boolean;
}) {
  const { currentUser } = useAuth();
  const [boards, setBoards] = useState<PinterestBoard[]>([]);
  const [isLoadingBoards, setIsLoadingBoards] = useState(false);
  const [boardError, setBoardError] = useState<string | null>(null);
  const hasUnsupportedMedia = mediaItems.some((item) => item.type !== "image");

  useEffect(() => {
    if (settings.link === undefined && defaultPinterestLink) {
      onSettingsChange({ link: defaultPinterestLink });
    }
  // `onSettingsChange` is passed inline by parent cards, so including it here
  // would reapply the default on every render before the setting lands in state.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultPinterestLink, settings.link]);

  useEffect(() => {
    let cancelled = false;

    const fetchBoards = async () => {
      if (!currentUser) return;
      setIsLoadingBoards(true);
      setBoardError(null);

      try {
        const idToken = await currentUser.getIdToken();
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE}/connections/pinterest/${connection.id}/boards`,
          {
            headers: {
              Authorization: `Bearer ${idToken}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to load Pinterest boards");
        }

        const data = await response.json();
        if (cancelled) return;

        const fetchedBoards = Array.isArray(data.boards) ? data.boards : [];
        setBoards(fetchedBoards);

        if (!settings.boardId && fetchedBoards.length > 0) {
          const defaultBoard = fetchedBoards[0];
          onSettingsChange({
            boardId: defaultBoard.id,
            boardName: defaultBoard.name,
          });
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Error loading Pinterest boards:", error);
          setBoardError("Could not load Pinterest boards");
        }
      } finally {
        if (!cancelled) {
          setIsLoadingBoards(false);
        }
      }
    };

    fetchBoards();

    return () => {
      cancelled = true;
    };
  // `onSettingsChange` is passed inline by parent cards, so including it here
  // would refetch boards on every render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connection.id, currentUser, settings.boardId]);

  return (
    <div className="space-y-4">
      <InheritedTitleField
        id={`pinterest-title-${connection.id}`}
        label="Title"
        value={settings.title}
        postTitle={postTitle}
        onChange={(title) => onSettingsChange({ title })}
        maxLength={100}
        placeholder="Enter Pinterest title"
      />

      <div className="space-y-2">
        <Label>Board</Label>
        <Select
          value={settings.boardId || ""}
          onValueChange={(boardId) => {
            const board = boards.find((item) => item.id === boardId);
            onSettingsChange({
              boardId,
              boardName: board?.name,
            });
          }}
          disabled={isLoadingBoards || boards.length === 0}
        >
          <SelectTrigger className="w-full">
            <SelectValue
              placeholder={
                isLoadingBoards ? "Loading boards..." : "Choose a board"
              }
            />
          </SelectTrigger>
          <SelectContent>
            {boards.map((board) => (
              <SelectItem key={board.id} value={board.id}>
                {board.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {boardError && (
          <p className="text-xs text-destructive">{boardError}</p>
        )}
        {!isLoadingBoards && !boardError && boards.length === 0 && (
          <p className="text-xs text-muted-foreground">
            Create a Pinterest board, then reconnect or reopen these settings.
          </p>
        )}
      </div>

      <PlatformDescriptionField
        platform="pinterest"
        connectionId={connection.id}
        description={settings.description}
        descriptionSource={settings.descriptionSource}
        postDescription={postDescription}
        isGenerating={isDescriptionGenerating}
        onDescriptionChange={(desc) =>
          onSettingsChange({
            description: desc,
            descriptionSource: desc.trim() ? "manual" : undefined,
          })
        }
      />

      <div className="space-y-2">
        <Label htmlFor={`pinterest-link-${connection.id}`}>Link</Label>
        <Input
          id={`pinterest-link-${connection.id}`}
          value={settings.link || ""}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            onSettingsChange({ link: e.target.value })
          }
          placeholder="https://..."
          type="url"
        />
        <p className="text-xs text-muted-foreground">
          Optional destination URL attached to each Pin image.
        </p>
      </div>

      {hasUnsupportedMedia && (
        <p className="text-xs text-yellow-700">
          Pinterest demo publishing currently supports image Pins and image
          carousels only.
        </p>
      )}
    </div>
  );
}

/**
 * Renders the platform-specific settings form for a single connection.
 * Used by the drill-down view in PostSettingsPanel.
 */
function PlatformSpecificConnectionSettings({
  connection,
  mediaItems,
  settings,
  onSettingsChange,
  postDescription,
  postTitle,
  defaultPinterestLink,
  isDescriptionGenerating = false,
}: {
  connection: PlatformConnection;
  mediaItems: MediaItem[];
  settings: PlatformSpecificSettings[keyof PlatformSpecificSettings];
  onSettingsChange: (
    settings: Partial<PlatformSpecificSettings[keyof PlatformSpecificSettings]>
  ) => void;
  postDescription: string;
  postTitle?: string;
  defaultPinterestLink?: string;
  isDescriptionGenerating?: boolean;
}) {
  switch (connection.platform) {
    case "tiktok":
      return (
        <TikTokConnectionSettings
          connection={connection}
          mediaItems={mediaItems}
          settings={settings as TiktokSettings}
          onSettingsChange={onSettingsChange}
          postDescription={postDescription}
          postTitle={postTitle}
        />
      );
    case "youtube":
      return (
        <YoutubeConnectionSettings
          connection={connection}
          mediaItems={mediaItems}
          settings={settings as YoutubeSettings}
          onSettingsChange={onSettingsChange}
          postDescription={postDescription}
          postTitle={postTitle}
        />
      );
    case "instagram":
      return (
        <InstagramConnectionSettings
          connection={connection}
          mediaItems={mediaItems}
          settings={settings as InstagramSettings}
          onSettingsChange={onSettingsChange}
          postDescription={postDescription}
        />
      );
    case "linkedin":
      return (
        <LinkedinConnectionSettings
          connection={connection}
          mediaItems={mediaItems}
          settings={settings as LinkedinSettings}
          onSettingsChange={onSettingsChange}
          postDescription={postDescription}
          postTitle={postTitle}
        />
      );
    case "facebook":
      return (
        <FacebookConnectionSettings
          connection={connection}
          mediaItems={mediaItems}
          settings={settings as FacebookSettings}
          onSettingsChange={onSettingsChange}
          postDescription={postDescription}
        />
      );
    case "pinterest":
      return (
        <PinterestConnectionSettings
          connection={connection}
          mediaItems={mediaItems}
          settings={settings as PinterestSettings}
          onSettingsChange={onSettingsChange}
          postDescription={postDescription}
          postTitle={postTitle}
          defaultPinterestLink={defaultPinterestLink}
          isDescriptionGenerating={isDescriptionGenerating}
        />
      );
    default:
      return null;
  }
}

/** Whether image slides go out as a rendered video ("Publish as" below). */
function rendersImagesAsVideo(
  platform: PlatformType,
  settings: unknown,
  isVideoPost: boolean
): boolean {
  return platform === "youtube" || (
    platform !== "pinterest" &&
    ((settings as VideoSettingsOverrides | undefined)?.videoEnabled ?? isVideoPost)
  );
}

/** Whether a channel will publish a video: an uploaded one, or rendered slides. */
export function channelPublishesVideo(
  platform: PlatformType,
  settings: unknown,
  mediaItems: MediaItem[],
  isVideoPost: boolean
): boolean {
  if (mediaItems.some((item) => item.type === "video")) return true;
  const imagePost = mediaItems.length > 0 && mediaItems.every((item) => item.type === "image");
  return imagePost && rendersImagesAsVideo(platform, settings, isVideoPost);
}

export function PlatformConnectionSettings(
  props: Parameters<typeof PlatformSpecificConnectionSettings>[0] & { isVideoPost?: boolean }
) {
  const { connection, mediaItems, settings, onSettingsChange, isVideoPost = false } = props;
  const imagePost = mediaItems.length > 0 && mediaItems.every(item => item.type === "image");
  const video = rendersImagesAsVideo(connection.platform, settings, isVideoPost);
  // Platform controls (reels, duet, carousel restrictions) describe the
  // selected output, even though the source slides remain images.
  const effectiveMedia = imagePost && video
    ? [{ ...mediaItems[0], type: "video" as const }]
    : mediaItems;

  return (
    <div className="space-y-4">
      {imagePost && (
        <div className="space-y-2">
          {(connection.platform === "youtube" || connection.platform === "pinterest") && <p className="text-xs font-medium text-muted-foreground">Publish as</p>}
          {connection.platform === "youtube" ? (
            <p className="text-sm text-muted-foreground">Video · YouTube only supports video</p>
          ) : connection.platform === "pinterest" ? (
            <p className="text-sm text-muted-foreground">{mediaItems.length > 1 ? "Carousel" : "Image"} · Pinterest video publishing is not available</p>
          ) : (
            <PostFormatToggle label="Publish as" mediaCount={mediaItems.length} video={video} onChange={videoEnabled => onSettingsChange({ videoEnabled })} />
          )}
        </div>
      )}
      <PlatformSpecificConnectionSettings {...props} mediaItems={effectiveMedia} />
    </div>
  );
}
